class ScProductZoom extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-sc-zoom-dialog]');
    this.stage = this.querySelector('[data-sc-zoom-stage]');
    this.gallery = this.closest('product-media-gallery');
    this.figures = Array.from(this.querySelectorAll('[data-sc-zoom-figure]'));
    if (!this.dialog || !this.stage || !this.gallery || !this.figures.length) return;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.index = 0;
    this.scale = 1;
    this.x = 0;
    this.y = 0;
    this.pointers = new Map();
    this.gallery.addEventListener('click', this.trigger, options);
    this.querySelector('[data-sc-zoom-close]').addEventListener('click', this.close, options);
    this.querySelector('[data-sc-zoom-previous]')?.addEventListener('click', () => this.select(this.index - 1), options);
    this.querySelector('[data-sc-zoom-next]')?.addEventListener('click', () => this.select(this.index + 1), options);
    this.querySelector('[data-sc-zoom-in]').addEventListener('click', () => this.zoom(this.scale + .5), options);
    this.querySelector('[data-sc-zoom-out]').addEventListener('click', () => this.zoom(this.scale - .5), options);
    this.querySelector('[data-sc-zoom-reset]').addEventListener('click', () => this.zoom(1), options);
    this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
    this.dialog.addEventListener('close', this.afterClose, options);
    this.dialog.addEventListener('keydown', this.keydown, options);
    this.dialog.addEventListener('click', event => {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
    }, options);
    this.stage.addEventListener('pointerdown', this.pointerDown, options);
    this.stage.addEventListener('pointermove', this.pointerMove, options);
    this.stage.addEventListener('pointerup', this.pointerUp, options);
    this.stage.addEventListener('pointercancel', this.pointerCancel, options);
    this.stage.addEventListener('wheel', this.wheel, { ...options, passive: false });
    this.stage.addEventListener('dragstart', event => event.preventDefault(), options);
    for (const image of this.querySelectorAll('.sc-image-viewer__image')) image.addEventListener('load', () => { if (image === this.image) this.fit(); }, options);
    this.resizeObserver = new ResizeObserver(() => { if (this.dialog.open) this.fit(); });
    this.resizeObserver.observe(this.stage);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
    clearTimeout(this.closeTimer);
    if (this.dialog?.open) this.dialog.close();
    this.releaseScroll();
  }

  trigger = event => {
    if (event.defaultPrevented || this.dialog.open) return;
    const trigger = event.target.closest('[data-zoom-trigger], [data-sc-zoom-trigger]');
    if (!trigger || !this.gallery.contains(trigger) || this.contains(trigger)) return;
    const index = this.figures.findIndex(figure => figure.dataset.mediaId === trigger.dataset.zoomMediaId);
    if (index < 0) return;
    event.preventDefault();
    this.opener = trigger.tagName === 'BUTTON' ? trigger : trigger.querySelector('[data-zoom-trigger], [data-sc-zoom-trigger]') || trigger;
    this.dialog.removeAttribute('data-closing');
    this.dialog.showModal();
    document.documentElement.classList.add('sc-image-viewer-is-open');
    this.select(index);
    this.querySelector('[data-sc-zoom-close]').focus({ preventScroll: true });
  };

  select(index) {
    this.index = (index + this.figures.length) % this.figures.length;
    this.figures.forEach((figure, i) => { figure.hidden = i !== this.index; });
    this.image = this.figures[this.index].querySelector('img');
    this.image.loading = 'eager';
    this.scale = 1;
    this.x = 0;
    this.y = 0;
    this.pointers.clear();
    this.gesture = null;
    this.lastTap = null;
    const label = this.dialog.dataset.counterLabel.replace('__CURRENT__', this.index + 1).replace('__TOTAL__', this.figures.length);
    this.querySelector('[data-sc-zoom-counter]').textContent = label;
    this.fit();
  }

  fit() {
    if (!this.image || !this.dialog.open) return;
    const width = this.stage.clientWidth;
    const height = this.stage.clientHeight;
    const naturalWidth = this.image.naturalWidth || Number(this.image.getAttribute('width')) || 1;
    const naturalHeight = this.image.naturalHeight || Number(this.image.getAttribute('height')) || 1;
    const fit = Math.min(width / naturalWidth, height / naturalHeight);
    this.fitWidth = naturalWidth * fit;
    this.fitHeight = naturalHeight * fit;
    this.image.style.width = `${this.fitWidth}px`;
    this.image.style.height = `${this.fitHeight}px`;
    this.draw();
  }

  draw() {
    if (!this.image) return;
    const maxX = Math.max(0, (this.fitWidth * this.scale - this.stage.clientWidth) / 2);
    const maxY = Math.max(0, (this.fitHeight * this.scale - this.stage.clientHeight) / 2);
    this.x = Math.max(-maxX, Math.min(maxX, this.x || 0));
    this.y = Math.max(-maxY, Math.min(maxY, this.y || 0));
    if (this.scale === 1) this.x = this.y = 0;
    this.image.style.transform = `translate(-50%, -50%) translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
    this.image.sizes = `${Math.ceil(this.fitWidth * this.scale)}px`;
    this.stage.toggleAttribute('data-zoomed', this.scale > 1.01);
    this.querySelector('[data-sc-zoom-percent]').textContent = `${Math.round(this.scale * 100)} %`;
    this.querySelector('[data-sc-zoom-out]').disabled = this.scale <= 1;
    this.querySelector('[data-sc-zoom-in]').disabled = this.scale >= 4;
  }

  point(clientX, clientY) {
    const rect = this.stage.getBoundingClientRect();
    return { x: clientX - rect.left - rect.width / 2, y: clientY - rect.top - rect.height / 2 };
  }

  zoom(scale, anchor = { x: 0, y: 0 }) {
    const next = Math.max(1, Math.min(4, scale));
    const ratio = next / this.scale;
    this.x = anchor.x - (anchor.x - this.x) * ratio;
    this.y = anchor.y - (anchor.y - this.y) * ratio;
    this.scale = next;
    this.draw();
  }

  pointerDown = event => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.stage.setPointerCapture(event.pointerId);
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointers.size === 2) {
      this.startPinch();
      return;
    }
    if (this.pointers.size !== 1) return;
    this.gesture = { type: 'single', pointerType: event.pointerType, startX: event.clientX, startY: event.clientY, startTime: performance.now(), baseX: this.x, baseY: this.y, scale: this.scale, moved: false };
  };

  startPinch() {
    const [a, b] = Array.from(this.pointers.values());
    const center = this.point((a.x + b.x) / 2, (a.y + b.y) / 2);
    this.gesture = { type: 'pinch', distance: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), scale: this.scale, contentX: (center.x - this.x) / this.scale, contentY: (center.y - this.y) / this.scale };
    this.lastTap = null;
    this.stage.setAttribute('data-dragging', '');
  }

  pointerMove = event => {
    if (!this.pointers.has(event.pointerId) || !this.gesture) return;
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const gesture = this.gesture;
    if (this.pointers.size >= 2 && gesture.type === 'pinch') {
      const [a, b] = Array.from(this.pointers.values());
      const center = this.point((a.x + b.x) / 2, (a.y + b.y) / 2);
      this.scale = Math.max(1, Math.min(4, gesture.scale * Math.hypot(a.x - b.x, a.y - b.y) / gesture.distance));
      this.x = center.x - gesture.contentX * this.scale;
      this.y = center.y - gesture.contentY * this.scale;
      this.draw();
    } else if (gesture.type === 'single') {
      const dx = event.clientX - gesture.startX;
      const dy = event.clientY - gesture.startY;
      if (Math.hypot(dx, dy) > 7) gesture.moved = true;
      if (this.scale > 1) {
        this.x = gesture.baseX + dx;
        this.y = gesture.baseY + dy;
        this.stage.setAttribute('data-dragging', '');
        this.draw();
      }
    }
  };

  pointerUp = event => {
    if (!this.pointers.has(event.pointerId)) return;
    const gesture = this.gesture;
    this.pointers.delete(event.pointerId);
    if (gesture?.type === 'pinch') {
      if (this.pointers.size >= 2) { this.startPinch(); return; }
      if (this.pointers.size === 1) {
        const [point] = this.pointers.values();
        this.gesture = { type: 'single', pointerType: event.pointerType, startX: point.x, startY: point.y, startTime: performance.now(), baseX: this.x, baseY: this.y, scale: this.scale, moved: true, suppressTap: true };
      } else this.gesture = null;
      this.stage.removeAttribute('data-dragging');
      return;
    }
    if (this.pointers.size || !gesture) return;
    this.gesture = null;
    this.stage.removeAttribute('data-dragging');
    const dx = event.clientX - gesture.startX;
    const dy = event.clientY - gesture.startY;
    const elapsed = performance.now() - gesture.startTime;
    if (!gesture.suppressTap && gesture.pointerType !== 'mouse' && gesture.scale === 1 && Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.3 && elapsed < 800) {
      this.select(this.index + (dx < 0 ? 1 : -1));
      return;
    }
    if (gesture.moved || gesture.suppressTap || Math.hypot(dx, dy) > 7 || elapsed > 500) return;
    const anchor = this.point(event.clientX, event.clientY);
    if (gesture.pointerType === 'mouse') {
      this.zoom(this.scale > 1.01 ? 1 : 2.5, anchor);
      return;
    }
    const now = performance.now();
    if (this.lastTap && now - this.lastTap.time < 330 && Math.hypot(event.clientX - this.lastTap.x, event.clientY - this.lastTap.y) < 30) {
      this.zoom(this.scale > 1.01 ? 1 : 2.5, anchor);
      this.lastTap = null;
    } else this.lastTap = { time: now, x: event.clientX, y: event.clientY };
  };

  pointerCancel = event => {
    this.pointers.delete(event.pointerId);
    this.gesture = null;
    this.lastTap = null;
    this.stage.removeAttribute('data-dragging');
  };

  wheel = event => {
    if (!this.dialog.open) return;
    event.preventDefault();
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.stage.clientHeight : 1;
    const delta = Math.max(-250, Math.min(250, event.deltaY * unit));
    this.zoom(this.scale * Math.exp(-delta * .002), this.point(event.clientX, event.clientY));
  };

  keydown = event => {
    if (!this.dialog.open) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.select(this.index + (event.key === 'ArrowRight' ? 1 : -1));
    } else if (event.key === '+' || event.key === '=') {
      event.preventDefault(); this.zoom(this.scale + .5);
    } else if (event.key === '-') {
      event.preventDefault(); this.zoom(this.scale - .5);
    } else if (event.key === '0') {
      event.preventDefault(); this.zoom(1);
    }
  };

  close = () => {
    if (!this.dialog.open || this.dialog.hasAttribute('data-closing')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.dialog.close();
      return;
    }
    this.dialog.setAttribute('data-closing', '');
    this.closeTimer = setTimeout(() => { if (this.dialog.open) this.dialog.close(); }, 180);
  };

  releaseScroll() {
    if (!document.querySelector('sc-product-zoom dialog[open]')) document.documentElement.classList.remove('sc-image-viewer-is-open');
  }

  afterClose = () => {
    clearTimeout(this.closeTimer);
    this.dialog.removeAttribute('data-closing');
    this.pointers.clear();
    this.gesture = null;
    this.stage.removeAttribute('data-dragging');
    this.releaseScroll();
    if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
  };
}

if (!customElements.get('sc-product-zoom')) customElements.define('sc-product-zoom', ScProductZoom);
